import React, { useEffect, useMemo, useState } from "react";
import { useGetAllCampaignBranchesQuery } from "../../../redux/services/campaignApi";
import { SearchableDropdownOption } from "../../SearchableDropdown/types";
import { Branch } from "../../../pages/Campaigns/utils/types";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import SearchIcon from "../../../assets/svg/search-icon";
import {
  selectCampaignsFilters,
  selectCampaignTemplatesFilters,
  setCampaignsBranch,
  setCampaignTemplatesBranch,
} from "../../../redux/slices/filterSlice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { setCurrentPage } from "../../../redux/slices/allCampaignSlice";

const CampaignBranchFilter = ({
  onClose,
  campaignTemplates = false,
}: {
  onClose: () => void;
  campaignTemplates?: boolean;
}) => {
  const dispatch = useDispatch();
  const campaignFilters = useSelector(
    campaignTemplates ? selectCampaignTemplatesFilters : selectCampaignsFilters
  );
  const category = useSelector(
    (state: RootState) => state.filter.campaigns.category.value
  );
  const useCase = useSelector(
    (state: RootState) => state.filter.campaigns.useCase.value
  );
  const {
    data: branches,
    isLoading,
    isFetching,
  } = useGetAllCampaignBranchesQuery({
    useCase: (useCase as string) || undefined,
    category: (category as string) || undefined,
  });
  const [selectedBranch, setSelectedBranch] = useState({
    label: campaignFilters.branch.displayValue,
    value: campaignFilters.branch.value,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredBranches, setFilteredBranches] = useState<
    SearchableDropdownOption[]
  >([]);
  const transformedBranches = useMemo(() => {
    return (branches?.branches ?? [])?.map((branch: Branch) => ({
      label: branch?.display_name,
      value: branch?.key,
    }));
  }, [branches]);

  const handleSearch = (query: string) => {
    if (!query.trim()) {
      setFilteredBranches(transformedBranches);
      return;
    }

    const filtered = transformedBranches?.filter(
      (user: SearchableDropdownOption) =>
        user.label?.toString().toLowerCase().includes(query.toLowerCase())
    );
    setFilteredBranches(filtered);
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  useEffect(() => {
    setFilteredBranches(transformedBranches);
  }, [transformedBranches]);

  const handleBranchSelect = (value: string, label: string) => {
    setSelectedBranch(
      value === selectedBranch.value
        ? { label: "", value: "" }
        : { label, value }
    );
  };

  const handleApply = () => {
    if (selectedBranch.value === campaignFilters.branch.value) {
      return;
    }
    dispatch(
      campaignTemplates
        ? setCampaignTemplatesBranch({
            value: selectedBranch.value as string,
            displayValue: selectedBranch.label as string,
          })
        : setCampaignsBranch({
            value: selectedBranch.value as string,
            displayValue: selectedBranch.label as string,
          })
    );
    dispatch(setCurrentPage(1));
    onClose();
  };

  return (
    <div className="flex flex-col">
      {/* Search Input */}
      <div className="relative border-b border-border-gray dark:border-border-gray-dk">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-placeholder dark:text-gray-placeholder-dk" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search user"
          className="w-full pl-10 pr-4 py-2 text-sm outline-none text-heading-text dark:text-heading-text-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk"
        />
      </div>

      {/* Selected User Display */}
      {selectedBranch.value && (
        <div className="flex items-center justify-between p-3 bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk border-b border-border-gray dark:border-border-gray-dk">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
              {
                transformedBranches?.find(
                  (u: SearchableDropdownOption) =>
                    u.value === selectedBranch.value
                )?.label
              }
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk" />
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="flex flex-col max-h-[600px] ">
        <div className="px-3 py-2 text-xs font-semibold text-dark-gray dark:text-dark-gray-dk uppercase border-b border-border-gray dark:border-border-gray-dk">
          All Users
        </div>

        <div className="overflow-y-auto">
          {isLoading || isFetching ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {isLoading ? "Loading users..." : "Fetching users..."}
              </div>
            </div>
          ) : filteredBranches?.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-gray-placeholder dark:text-gray-placeholder-dk">
                {searchQuery.trim() ? "No users found" : "No users available"}
              </div>
            </div>
          ) : (
            filteredBranches
              ?.filter(
                (user: SearchableDropdownOption) =>
                  user?.value !== selectedBranch?.value
              )
              ?.map((user: SearchableDropdownOption) => (
                <div
                  key={user.value}
                  onClick={() =>
                    handleBranchSelect(user.value, user?.label || "")
                  }
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-hover-gray dark:hover:bg-hover-gray-dk border-b border-border-gray dark:border-border-gray-dk last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-md text-heading-text dark:text-heading-text-dk truncate">
                      {user.label}
                    </div>
                  </div>
                  {selectedBranch.value === user.value && (
                    <CheckIcon className="w-4 h-4 text-verba-blue dark:text-verba-blue-dk shrink-0 ml-2" />
                  )}
                </div>
              ))
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-4 px-3 pb-2">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={onClose}
        />
        <Button
          innerText="Apply"
          disabled={selectedBranch?.value === campaignFilters?.branch?.value}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default CampaignBranchFilter;
